import { Box, Card, CardContent, Divider, useMediaQuery, useTheme } from "@mui/material";
import { useDispatch } from "../../app/helpers";
import { setInputs } from "../../features/Booking-Form/slices/booking.slice";
import { FormConfirmingSpinner } from "./partial/form-confirming-spinner.partial";
import { ResourceCateringQuantityField } from "./partial/resource-catering-quantity-field.partial";
import { ResourceInformationCardContents } from "./resource-information-card-contents.component";
import { NotSerializedBookingInputs } from "../../features/Booking-Form/typings/booking-inputs";
import { EnlargedImageCarousel } from "../../features/EnlargedImageCarousel/enlarged-image-carousel.component";
import { normalizePicturePaths } from "./functions/normalizePicturePaths.function";

type FormResourceConfirmProps = {
  confirmingBooking: boolean;
  bookingInputs: NotSerializedBookingInputs;
  onCateringQuantityValidityChange?: (invalid: boolean) => void;
};

export function FormResourceConfirm({
  confirmingBooking,
  bookingInputs,
  onCateringQuantityValidityChange
}: Readonly<FormResourceConfirmProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const resource = bookingInputs.bookedResource;
  const isCateringResource = resource?.resourceKind === "catering";

  if (!resource || confirmingBooking) {
    return <FormConfirmingSpinner />;
  }

  const picturePaths = normalizePicturePaths(resource.picturePaths);
  const hasPictures = picturePaths != null && picturePaths.length > 0;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 2,
        mt: 1,
        border: cardTheme => `1px solid ${cardTheme.palette.divider}`
      }}
      data-testid="form-resource-confirm-card"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: 0
        }}
      >
        <CardContent
          sx={{
            flex: hasPictures ? { xs: "0 0 auto", md: "1 1 50%" } : 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            pb: { xs: 0, md: 3 }
          }}
        >
          <ResourceInformationCardContents
            resourceInfo={resource}
            trailingDetail={
              isCateringResource ? (
                <ResourceCateringQuantityField
                  initialQty={bookingInputs.orderedQty ?? 1}
                  onQuantityChange={quantity => {
                    dispatch(setInputs({ orderedQty: quantity }));
                  }}
                  onValidityChange={onCateringQuantityValidityChange}
                />
              ) : null
            }
          />
        </CardContent>

        {hasPictures ? (
          <>
            <Divider sx={{ display: { xs: "block", md: "none" } }} />

            <Box
              sx={{
                flex: { xs: "0 0 auto", md: "0 0 40%" },
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 3, md: 2 }
              }}
            >
              <EnlargedImageCarousel
                picturePaths={picturePaths}
                isLoading={false}
                imageSize={isMobile ? 36 : 28}
                boxSize={isMobile ? 42 : 32}
              />
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  );
}
